
import { Ref, forwardRef, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ScheduleFormType, TierFormType, TierNotificationActions } from "../TierEditorUtils";
import { ContentContainer } from "../../../content-container/ContentContainer";
import { ContentSection } from "../../../content-section/ContentSection";
import { Box, Button, Stack, Typography } from "@mui/material";
import FormCheckbox from "../../../forms/form-checkbox/FormCheckbox";
import { AlarmScheduleCard } from "../../../alarm-schedule-card/AlarmScheduleCard";
import { ALWAYS_SCHEDULE  } from "../../../../services/alarm-scheme-data-service/Models";
import FormNotificationLists from "../../../form-notification-lists/FormNotificationLists";
import SideSheet from "../../../side-sheet/side-sheet/SideSheet";
import { isMobile, isTablet } from "react-device-detect";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../../common/utilities/GetObjectTypeName";
import OrganizationPicker from "../../../organization-picker/OrganizationPicker";
import SideSheetFilterInput from "../../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../../../side-sheet/side-sheet-switch/SideSheetSwitch";
import { IOrganization } from "../../../../services/organization-data-service/Models";
import NoData from "../../../no-data/NoData";
import { ViewOptionStateEnum } from "../../../contextual-app-bar/ViewOptionStateEnum";
import NotificationListEditorDialog, { bindNotificationListEditorDialog, useNotificationListEditorDialogState } from "../../../notification-list-editor/NotificationListEditorDialog";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { IReferenceNotificationListDTO } from "../../../../services/setpoint-administration-service/Models";
import { BaseActions } from "../../../action-menu/ActionMenuItem";
import ScheduledNotificationEditorDialog, { bindScheduledNotificationEditorDialog, useScheduledNotificationEditorDialogState } from "../scheduled-notification-editor/ScheduledNotificationEditorDialog";
import { ScheduleActions } from "../../../alarm-schedule-card/AlarmScheduleCardUtils";
import { useFormNotificationListsState } from "../../../form-notification-lists/FormNotificationListsState";
import { useAlarmSchemeReferenceData } from "../../../../queries/alarm-scheme-admin/UseAlarmSchemeReferenceData";

export type TierNotificationsType = {
    executeAction: (action: TierNotificationActions | BaseActions) => void;
};

export type TierNotificationsProps = {
     groupID: number,
     organizationID: number,
     viewOptionState: ViewOptionStateEnum;
     setViewOptionState: (state: ViewOptionStateEnum) => void;
     openSideSheet: boolean;
     onCloseSideSheet?: () => void;
 };

function TierNotifications({
    groupID,
    organizationID,
    viewOptionState,
    setViewOptionState,
    openSideSheet,
    onCloseSideSheet
}: TierNotificationsProps, ref: Ref<unknown> | undefined) {
    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();

    const{ state,
        setNotificationListFilterText,
        setOrganization,
        setViewSelected,
        resetState
    } = useFormNotificationListsState();

    useImperativeHandle<unknown, TierNotificationsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {data: referenceData, refetch} = useQueryNotifications(useAlarmSchemeReferenceData)({
        groupID: groupID,
        //organizationID: group?.organizationID ?? -1,
        organizationID: organizationID,
        //enabled: group !== undefined
    });

    const availableSchedules = referenceData?.schedules.filter(o => o.id !== ALWAYS_SCHEDULE) ?? [];


    const notificationListEditorDialog = useNotificationListEditorDialogState();    
    const scheduledNotificationEditorDialog = useScheduledNotificationEditorDialogState();

    const {
        //getValues,
        watch,
        trigger,
        control,
        //reset,
        //setValue,
        //formState: {
        //    errors
        //}
        //resetField
    } = useFormContext<TierFormType>();    


    const {
        //fields: notificationListFields,
        append,
        //remove:
        //update
    } = useFieldArray({
        control: control,
        name: 'notificationLists',
        keyName: "fieldKey"
    });    


    const {
        //fields: notificationListFields,
        append: appendSchedule,
        remove: removeSchedule,
        update: updateSchedule
    } = useFieldArray({
        control: control,
        name: 'schedules',
        keyName: "fieldKey"
    });    


    const tier = useWatch({ control: control }) as TierFormType;

   //console.log(`useschenot ${tier.useScheduledNotifications}`)

    const [
        useScheduledNotifications,
        notificationLists, 
        schedules
    ] = watch([
        'useScheduledNotifications',
        'notificationLists',
        'schedules'
    ]);

    const selectedNotificationListCount = notificationLists.filter(o => o.selected).length;
    const enableViewSelected = selectedNotificationListCount > 0;

    const tierSchedules = tier.schedules.filter(o => o.id !== ALWAYS_SCHEDULE);


    function executeAction(action: TierNotificationActions | BaseActions) {

        switch(action) {
            case TierNotificationActions.Add:
                onAddNotificationList();
                break;

            case TierNotificationActions.AddScheduledNotification:
                onAddScheduledNotification();
                break;
        }
    }

    function onScheduleAction(action: ScheduleActions, item: ScheduleFormType) {
        switch (action) {
            case ScheduleActions.Edit:
                onEditScheduledNotification(item);
                break;

            case ScheduleActions.Delete:
                break;
        }
    }


    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {
        setNotificationListFilterText(value);
    }


    async function onAddNotificationList() {

        //const orgID = (state.organizationID ?? group?.organizationID) as number;
        const orgID = (state.organizationID ?? organizationID) as number;

        const result = await notificationListEditorDialog.open({
            groupID: groupID,
            organizationID: orgID,
            notificationListID: -1,
        });

        if (result.reason === "save") {

            const values = result.values?.notificationList;
            if (values) {
                const newNotificationList: IReferenceNotificationListDTO = {
                    id: values.id,
                    name: values.name,
                    organizationID: orgID,
                    selected: false
                };

                append(newNotificationList);

                trigger('notificationLists');

                await refetch();
            }
        }
    }

    async function onAddScheduledNotification() {

        const result = await scheduledNotificationEditorDialog.open({
            groupID: groupID,
            organizationID: organizationID,
            item: {
                id: -1,
                name: '',
                selected: false,
                notificationLists: []
            } as ScheduleFormType,
        });

        if (result.reason === "save") {

            const values = result.values?.schedule;
            if (values) {

                values.selected = true;

                const ndx = schedules.findIndex(o => o.id === ALWAYS_SCHEDULE);
                if (ndx !== -1) {
                    removeSchedule(ndx);
                }

                appendSchedule(values);

                trigger('schedules');
            }
        }
    }

    async function onEditScheduledNotification(schedule: ScheduleFormType) {

        const result = await scheduledNotificationEditorDialog.open({
            groupID: groupID,
            organizationID: organizationID,
            item: {...schedule},
        });

        if (result.reason === "save") {

            const values = result.values?.schedule;
            if (values) {
                const ndx = schedules.findIndex(o => o.id === schedule.id);
                updateSchedule(ndx, values);

                trigger('schedules');
            }
        }
    }

    // function onUseScheduledNotificationsChanged() {

    //     const checked = getValues('useScheduledNotifications');


    //     const t = getValues('tiers');
    //     t.forEach(o => o.schedules = []);
    //     setValue('tiers', t, {shouldDirty: true});

    //     if (checked) {
    //         const list = getValues('notificationLists');
    //         list.forEach(o => o.selected = false);
    //         setValue('notificationLists', list, {shouldDirty: true});
    //     } else {

    //         const list = getValues('schedules');
    //         list.flatMap(o => o.notificationLists).forEach(o => o.selected = false);
    //         setValue('schedules', list, {shouldDirty: true});
    //     }
    // }


    useEffect(() => {

        if (useScheduledNotifications) {
            setViewOptionState(ViewOptionStateEnum.Hidden);
        } else if (viewOptionState !== state.viewOptionState) {
            setViewOptionState(state.viewOptionState);
        }
    }, [
        viewOptionState,
        state.viewOptionState,
        setViewOptionState,
        useScheduledNotifications
    ]);


   return (
       <ContentContainer>
           <ContentSection sx={{
               //padding: 2,
               paddingLeft: 2,
               paddingTop: 0,
               paddingBottom: 0,
               overflowY: 'auto'
           }}>
               
               <Stack
                   direction='row'
                   sx={{
                       justifyContent: isPhone ? 'flex-start' : availableSchedules.length > 0 ? 'space-between' : 'flex-end',
                       marginTop: 1,
                       marginBottom: 1
                   }}>

                   {
                   availableSchedules.length > 0 && 
                   <FormCheckbox
                       name="useScheduledNotifications"
                       label={t("alarmSchemeAdmin.tiers.useScheduledNotifications")}
                       //onChange={() => onUseScheduledNotificationsChanged()}
                       />
                   }

                   {!isPhone && !useScheduledNotifications && 
                       <Button
                           onClick={() => onAddNotificationList()}>
                           {t('buttons.newNotificationList')}
                       </Button>
                   }

                   {!isPhone && useScheduledNotifications && availableSchedules.length > 0 &&
                       <Button
                           onClick={() => onAddScheduledNotification()}>
                           {t('alarmSchemeAdmin.tiers.buttons.addScheduledNotification')}
                       </Button>
                   }

               </Stack>

               <Stack
                   direction='row'
                   sx={{
                       display: 'flex',
                       flex: '1 1 auto'
                   }}>

               <Box
                   sx={{
                   position: 'relative',
                   display: 'flex',
                   flexDirection: "column",
                   flex: '1 1 auto',
                   overflow: "hidden",
                   height: "100%",
                   }}>

                   {
                   !useScheduledNotifications ? 

                   <>
                       <Typography variant="subtitle1" sx={{
                           fontWeight: 600
                       }} >
                           {getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)}
                       </Typography>

                       <FormNotificationLists 
                           visible={true}
                           //ref={notificationList}
                           groupID={groupID}
                           filterText={state.notificationListFilterText}
                           viewSelected={state.viewSelected}
                           organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                           allowEdit={false}
                       />
                   </>

                   :

                   tierSchedules.length > 0 ? tierSchedules.map((o, ndx) => (
                       <AlarmScheduleCard 
                           key={ndx} 
                           item={o} 
                           sx={{ 
                               marginLeft: 0
                           }} 
                           onAction={onScheduleAction}
                           
                           />
                   ))
                   :

                   <NoData text={t('alarmSchemeAdmin.tiers.noScheduledNotifications')} />

                   }
                   
               </Box>

               { 
               !useScheduledNotifications &&
               <SideSheet
                   title={t("options")}
                   open={openSideSheet ?? false}
                   hideCloseButton={!isPhone}
                   onClose={() => onCloseSideSheet && onCloseSideSheet()}
                   onReset={resetState}
               >

                   <OrganizationPicker 
                       label={getObjectTypeName(eLynxObjectTypeEnum.Organization)}
                       color="inherit"
                       //organizationID={state.organizationID ?? group?.organizationID as number} 
                       organizationID={state.organizationID ?? -1 } 
                       onSelected={onOrganizationChanged}
                       includeAllOption={true}
                       />


                   <SideSheetSwitch
                       label={t("setpointAdmin.viewSelected", {name: getObjectTypeName(eLynxObjectTypeEnum.NotificationList,  true)})}
                       checked={state.viewSelected}
                       onChange={(checked: boolean) => {
                           onViewSelected(checked);
                       }}
                       disabled={!enableViewSelected} />

                   <SideSheetFilterInput
                       label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(eLynxObjectTypeEnum.NotificationList)})}
                       value={
                           state.notificationListFilterText}
                       onChange={(value) => {
                           onSetFilter(value);
                       }} 
                       disabled={state.viewSelected}/>

               </SideSheet>
               }

               </Stack>

           </ContentSection>
           <NotificationListEditorDialog {...bindNotificationListEditorDialog(notificationListEditorDialog)} />
           <ScheduledNotificationEditorDialog {...bindScheduledNotificationEditorDialog(scheduledNotificationEditorDialog)} />
       </ContentContainer>
   );
  }

  export default forwardRef(TierNotifications);